import type React from "react";

import {
  type InfiniteData,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { useAgentExecutionClient } from "src/api/agents/execution";
import {
  type AgentInvokeResponse,
  type Data,
  Environment,
  ExecutionMode,
  HumanMessageTypeEnum,
  type SessionMessagesResponse,
  type SessionMessagesResponseMessagesInner,
  SessionStatus,
} from "src/clients/agent-management";
import { toastActions } from "src/design-system/Toast/utils";

export const SESSION_CACHE_KEY_PREFIX = "agent-execution";

export const TERMINAL_STATUSES = new Set<string>([
  SessionStatus.SUCCESS,
  SessionStatus.FAILED,
  SessionStatus.CANCELLED,
]);

export const copilotSessionKeys = {
  messages: (sessionId: string | null) =>
    [SESSION_CACHE_KEY_PREFIX, "sessions", sessionId, "messages"] as const,
};

const PAGE_SIZE = 50;

// Fail-safe upper bound on awaiting-session polling — must exceed worst-case
// cold-start latency so the first chat doesn't stop polling before the
// response arrives (OPTI-7068).
export const AWAITING_SESSION_TIMEOUT_MS = 60_000;

export const fetchSessionMessages = async ({
  client,
  sessionId,
  pageParam,
}: {
  client: NonNullable<ReturnType<typeof useAgentExecutionClient>["client"]>;
  sessionId: string;
  pageParam?: string;
}): Promise<SessionMessagesResponse> => {
  try {
    const response =
      await client.listSessionMessagesCopilotsManagementApiV0SessionsSessionIdMessagesGet(
        {
          sessionId,
          pageSize: PAGE_SIZE,
          cursor: pageParam ?? undefined,
        },
      );
    return response.data;
  } catch (error) {
    if (!pageParam && isAxiosError(error) && error.response?.status === 404) {
      return {
        session: undefined,
        messages: [],
      } as unknown as SessionMessagesResponse;
    }
    throw error;
  }
};

export const preserveCachedMessages = (
  data: SessionMessagesResponse,
  queryClient: QueryClient,
  queryKey: readonly unknown[],
): SessionMessagesResponse => {
  const serverMessages = data.messages ?? [];
  const cached =
    queryClient.getQueryData<InfiniteData<SessionMessagesResponse>>(queryKey);
  const cachedMessages = cached?.pages?.[0]?.messages ?? [];

  if (serverMessages.length === 0 && cachedMessages.length > 0) {
    return cached!.pages[0];
  }

  const optimisticMsg = cachedMessages[0];
  const hasOptimisticMessage = optimisticMsg != null && !optimisticMsg.id;
  if (hasOptimisticMessage) {
    const optimisticText =
      optimisticMsg.type === "human"
        ? optimisticMsg.invocation_message
        : undefined;
    const isEchoOf = (m: SessionMessagesResponseMessagesInner) =>
      m.type === "human" &&
      m.id != null &&
      m.invocation_message === optimisticText;
    const serverEchoed =
      optimisticText != null &&
      serverMessages.filter(isEchoOf).length >
        cachedMessages.filter(isEchoOf).length;
    if (!serverEchoed) {
      return {
        ...cached!.pages[0],
        session: data.session,
        next_cursor: data.next_cursor,
      } as SessionMessagesResponse;
    }
  }

  return data;
};

export const copilotMessagesQueryBase = ({
  sessionId,
  client,
  queryClient,
}: {
  sessionId: string | null;
  client: ReturnType<typeof useAgentExecutionClient>["client"];
  queryClient: QueryClient;
}) => {
  const queryKey = copilotSessionKeys.messages(sessionId);
  return {
    queryKey,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: SessionMessagesResponse) =>
      lastPage.next_cursor ?? undefined,
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      if (!client) throw new Error("Agent execution client not available");
      if (!sessionId) throw new Error("Session ID not available");

      const data = await fetchSessionMessages({ client, sessionId, pageParam });
      if (!pageParam) {
        return preserveCachedMessages(data, queryClient, queryKey);
      }
      return data;
    },
  };
};

type UseCopilotMessagesOptions<TData = InfiniteData<SessionMessagesResponse>> =
  {
    enabled?: boolean;
    pollingInterval?: number;
    awaitingSessionRef?: React.MutableRefObject<boolean>;
    isPending?: boolean;
    select?: (data: InfiniteData<SessionMessagesResponse>) => TData;
  };

export const useCopilotMessages = <
  TData = InfiniteData<SessionMessagesResponse>,
>(
  sessionId: string | null,
  options?: UseCopilotMessagesOptions<TData>,
) => {
  const {
    enabled = true,
    pollingInterval = 1000,
    awaitingSessionRef,
    isPending = false,
    select,
  } = options ?? {};

  const { client } = useAgentExecutionClient();
  const queryClient = useQueryClient();

  const queryBase = copilotMessagesQueryBase({
    sessionId,
    client,
    queryClient,
  });

  return useInfiniteQuery({
    ...queryBase,
    enabled: enabled && !!client && !!sessionId,
    select,
    refetchInterval: (query) => {
      if (query.state.status === "error") return false;
      if (isPending) return false;

      const sessionStatus = query.state.data?.pages?.[0]?.session?.status;

      if (sessionStatus && !TERMINAL_STATUSES.has(sessionStatus)) {
        if (awaitingSessionRef?.current) {
          awaitingSessionRef.current = false;
        }
        return pollingInterval;
      }

      return awaitingSessionRef?.current ? pollingInterval : false;
    },
  });
};

type UseCopilotInvokeParams = {
  agentId: string | undefined;
  agentVersionId: string | undefined;
  sessionId: string | null;
  source: string;
  environment?: Environment;
  extraInvokeData?: Record<string, unknown>;
  messagesQueryKey: readonly unknown[];
  onInvokeStarted?: () => void;
};

export const useCopilotInvoke = ({
  agentId,
  agentVersionId,
  sessionId,
  source,
  environment = Environment.SANDBOX,
  extraInvokeData,
  messagesQueryKey,
  onInvokeStarted,
}: UseCopilotInvokeParams) => {
  const { client } = useAgentExecutionClient();
  const queryClient = useQueryClient();

  type MutationContext = {
    previousData: InfiniteData<SessionMessagesResponse> | undefined;
  };

  return useMutation<AgentInvokeResponse, Error, string, MutationContext>({
    mutationFn: async (
      invocationMessage: string,
    ): Promise<AgentInvokeResponse> => {
      if (!client) {
        throw new Error("Agent execution client not available");
      }
      if (!agentId) {
        throw new Error("Agent ID is required");
      }
      if (!sessionId) {
        throw new Error("Session ID is required");
      }

      const data = {
        agent_id: agentId,
        agent_version_id: agentVersionId ?? "latest",
        session_id: sessionId,
        invocation_message: invocationMessage,
        type: "invoke",
        source,
        control: {
          execution_mode: ExecutionMode.ASYNC,
          environment,
        },
        ...extraInvokeData,
      } as unknown as Data;

      const response =
        await client.invokeAgentCopilotsManagementApiV0InvokePost({
          data,
        });
      return response.data;
    },
    onMutate: async (invocationMessage: string) => {
      await queryClient.cancelQueries({ queryKey: messagesQueryKey });

      const previousData =
        queryClient.getQueryData<InfiniteData<SessionMessagesResponse>>(
          messagesQueryKey,
        );

      queryClient.setQueryData<InfiniteData<SessionMessagesResponse>>(
        messagesQueryKey,
        (old) => {
          const firstPage = old?.pages[0];
          const optimisticFirstPage: SessionMessagesResponse = {
            ...firstPage,
            session: firstPage?.session
              ? { ...firstPage.session, status: SessionStatus.RUNNING }
              : firstPage?.session,
            messages: [
              {
                type: HumanMessageTypeEnum.HUMAN,
                invocation_message: invocationMessage,
                content: invocationMessage,
                token_count: 0,
              } as SessionMessagesResponseMessagesInner,
              ...(firstPage?.messages ?? []),
            ],
          } as SessionMessagesResponse;

          return {
            pages: [optimisticFirstPage, ...(old?.pages.slice(1) ?? [])],
            pageParams: old?.pageParams ?? [undefined],
          };
        },
      );

      return { previousData };
    },
    onSuccess: () => {
      onInvokeStarted?.();
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData !== undefined) {
        queryClient.setQueryData(messagesQueryKey, context.previousData);
      }
      toastActions.failure({
        title: "Failed to send message",
        description: "Please try again",
      });
    },
  });
};
